import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BeforeAfterSlider({ beforeUrl, afterUrl }) {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  };

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e) => { if (isDragging.current) handleMove(e.clientX); };
  const handleTouchMove = (e) => { handleMove(e.touches[0].clientX); };

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  });

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] overflow-hidden rounded-xl cursor-col-resize select-none"
      onMouseDown={handleMouseDown}
      onTouchMove={handleTouchMove}
      data-testid="before-after-slider"
    >
      {/* After image (full background) */}
      <img src={afterUrl} alt="Etter" className="absolute inset-0 w-full h-full object-cover" />

      {/* Before image (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
        <img src={beforeUrl} alt="For" className="absolute inset-0 w-full h-full object-cover" style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%', maxWidth: 'none' }} />
      </div>

      {/* Slider handle */}
      <div className="absolute top-0 bottom-0" style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
        <div className="w-1 h-full bg-white shadow-lg" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
          <ChevronLeft size={14} className="text-stone-700 -mr-1" />
          <ChevronRight size={14} className="text-stone-700 -ml-1" />
        </div>
      </div>

      {/* Labels */}
      <div className="absolute bottom-3 left-3 bg-stone-900/80 text-white px-3 py-1 rounded-full text-xs font-semibold tracking-wide pointer-events-none">
        FØR
      </div>
      <div className="absolute bottom-3 right-3 bg-emerald-800/80 text-white px-3 py-1 rounded-full text-xs font-semibold tracking-wide pointer-events-none">
        ETTER
      </div>
    </div>
  );
}

function Gallery() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/projects/public`);
        if (res.ok) setProjects(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const municipalities = [...new Set(projects.map(p => p.municipality))].sort();
  const filtered = filter ? projects.filter(p => p.municipality === filter) : projects;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-testid="gallery-back-btn">
            <ArrowLeft size={20} />
            <span className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Gravsteinoppfriskeren
            </span>
          </button>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-stone-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="gallery-page-title">
            Før og etter
          </h1>
          <p className="text-base md:text-lg text-stone-300 max-w-2xl mx-auto">
            Se hvordan vi forvandler slitne gravsteiner til vakre minnesmerker igjen.
            Dra i slideren for å se forskjellen.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filter */}
        {municipalities.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
            <button
              onClick={() => setFilter('')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                !filter ? 'bg-emerald-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
              }`}
            >
              Alle ({projects.length})
            </button>
            {municipalities.map(m => (
              <button
                key={m}
                onClick={() => setFilter(m)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === m ? 'bg-emerald-800 text-white' : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
                }`}
              >
                {m} ({projects.filter(p => p.municipality === m).length})
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-800 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-500">Laster galleri...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="gallery-empty">
            <p className="text-stone-500 text-lg">Ingen prosjekter å vise ennå.</p>
            <p className="text-stone-400 mt-2">Kom tilbake snart for å se våre oppfriskinger!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="gallery-grid">
            {filtered.map((project) => {
              const beforeImgs = (project.images || []).filter(i => i.category === 'before').sort((a, b) => a.position - b.position);
              const afterImgs = (project.images || []).filter(i => i.category === 'after').sort((a, b) => a.position - b.position);
              const mainBefore = beforeImgs[0]?.image_url;
              const mainAfter = afterImgs[0]?.image_url;

              if (!mainBefore || !mainAfter) return null;

              return (
                <div key={project.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-testid={`gallery-project-${project.id}`}>
                  <BeforeAfterSlider beforeUrl={mainBefore} afterUrl={mainAfter} />
                  <div className="p-6">
                    {project.gravestone_name && (
                    <h3 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {project.gravestone_name}
                    </h3>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-500">
                      {(project.cemetery || project.municipality) && (
                        <span className="flex items-center gap-1"><MapPin size={14} /> {[project.cemetery, project.municipality].filter(Boolean).join(', ')}</span>
                      )}
                      {project.work_date && <span className="flex items-center gap-1"><Calendar size={14} /> {project.work_date}</span>}
                    </div>
                    {project.notes && <p className="text-sm text-stone-400 mt-3 italic">{project.notes}</p>}

                    {/* Additional images thumbnails */}
                    {(beforeImgs.length > 1 || afterImgs.length > 1) && (
                      <div className="flex gap-2 mt-4 overflow-x-auto">
                        {beforeImgs.slice(1).map((img, i) => (
                          <img key={`b-${i}`} src={img.image_url} alt={`For ${i+2}`} className="w-16 h-16 rounded-lg object-cover border-2 border-stone-200 flex-shrink-0" />
                        ))}
                        {afterImgs.slice(1).map((img, i) => (
                          <img key={`a-${i}`} src={img.image_url} alt={`Etter ${i+2}`} className="w-16 h-16 rounded-lg object-cover border-2 border-emerald-200 flex-shrink-0" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="bg-emerald-900 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Trenger gravsteinen din oppfrisking?
          </h2>
          <p className="text-emerald-100 mb-6">Kontakt oss for et uforpliktende pristilbud</p>
          <a href="tel:48147777" className="inline-block bg-white text-emerald-900 hover:bg-stone-100 rounded-full px-8 py-3 font-semibold transition-colors">
            Ring 48 14 77 77
          </a>
        </div>
      </div>
    </div>
  );
}

export default Gallery;
